import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { serviceCardStyles, shadows } from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { ColorField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";
import { ImageUpload } from "../ui/ImageUpload";

export interface ServiceCardProps {
  title: string;
  description: string;
  icon?: string;
  imageUrl?: string;
  link?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  showShadow?: boolean;
  padding?: number;
  textFontFamily?: string;
}

export const ServiceCard = ({
  title = "Service Name",
  description = "Brief description of the service we provide to our patients.",
  icon,
  imageUrl,
  link,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  borderRadius = 12,
  showShadow = true,
  padding = 24,
  textFontFamily,
}: ServiceCardProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Responsive values from config
  const safePadding = padding || serviceCardStyles.padding.desktop;
  const mobilePadding = isMobile ? serviceCardStyles.padding.mobile : safePadding;
  const imageHeight = isMobile ? serviceCardStyles.imageHeight.mobile : serviceCardStyles.imageHeight.desktop;

  const cardContent = (
    <>
      {imageUrl && (
        <Box
          sx={{
            mb: isMobile ? "12px" : "16px",
            mx: isMobile ? `-${mobilePadding}px` : `-${safePadding}px`,
            mt: isMobile ? `-${mobilePadding}px` : `-${safePadding}px`,
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={{
              width: "100%",
              height: `${imageHeight}px`,
              objectFit: "cover",
              borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
            }}
          />
        </Box>
      )}
      {icon && !imageUrl && <Box sx={{ fontSize: isMobile ? "1.75rem" : "2.25rem", mb: 2 }}>{icon}</Box>}
      <Element
        id="card-title"
        is={Text}
        text={title}
        fontSize={20}
        fontWeight="semibold"
        tagName="h4"
        color={textColor}
        fontFamily={textFontFamily}
      />
      <Element
        id="card-desc"
        is={Text}
        text={description}
        fontSize={16}
        tagName="p"
        color={textColor}
        fontFamily={textFontFamily}
      />
      {link && (
        <Box sx={{ mt: 2 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              color: "var(--color-primary, #3b82f6)",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Learn More →
          </Typography>
        </Box>
      )}
    </>
  );

  const cardStyles = {
    bgcolor: backgroundColor,
    borderRadius: `${borderRadius}px`,
    p: imageUrl ? 0 : `${mobilePadding}px`,
    pb: `${mobilePadding}px`,
    boxShadow: showShadow ? shadows.md : "none",
    overflow: "hidden",
    // Fixed sizing
    flex: "0 1 170px",
    width: "170px",
    minWidth: 100,
    maxWidth: 200,
    boxSizing: "border-box",
    ...(selected && selectedOutlineSx),
  };

  const innerPadding = imageUrl ? (isMobile ? `${Math.max(12, safePadding * 0.6)}px` : `${safePadding}px`) : 0;

  if (link) {
    return (
      <Box
        component="a"
        className="service-card"
        ref={(ref: HTMLAnchorElement | null) => {
          if (ref) {
            connect(drag(ref));
            (containerRef as React.MutableRefObject<HTMLElement | null>).current = ref;
          }
        }}
        href={link}
        onClick={handleLinkClick}
        sx={{
          ...cardStyles,
          display: "block",
          textDecoration: "none",
          transition: "transform 0.2s",
          "&:hover": { transform: "scale(1.02)" },
        }}
      >
        <Box sx={{ p: innerPadding }}>{cardContent}</Box>
      </Box>
    );
  }

  return (
    <Box
      className="service-card"
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={cardStyles}
    >
      <Box sx={{ p: innerPadding }}>{cardContent}</Box>
    </Box>
  );
};

export const ServiceCardSettings = () => {
  const {
    actions: { setProp },
    icon,
    imageUrl,
    link,
    backgroundColor,
    textColor,
    borderRadius,
    showShadow,
    padding,
  } = useNode((node) => ({
    icon: node.data.props.icon,
    imageUrl: node.data.props.imageUrl,
    link: node.data.props.link,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    showShadow: node.data.props.showShadow,
    padding: node.data.props.padding,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <ImageUpload
        label="Image (overrides icon)"
        value={imageUrl || ""}
        onChange={(url) => setProp((props: ServiceCardProps) => (props.imageUrl = url || undefined))}
      />

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: ServiceCardProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: ServiceCardProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: ServiceCardProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <SliderField
        label="Padding"
        value={padding}
        onChange={(v) => setProp((props: ServiceCardProps) => (props.padding = v))}
        min={8}
        max={48}
        unit="px"
      />

      <CheckboxField
        label="Show Shadow"
        checked={showShadow}
        onChange={(v) => setProp((props: ServiceCardProps) => (props.showShadow = v))}
      />
    </SettingsSection>
  );
};

ServiceCard.craft = {
  displayName: "Service Card",
  isCanvas: true,
  props: {
    title: "Service Name",
    description: "Brief description of the service we provide to our patients.",
    icon: "🏥",
    imageUrl: undefined,
    link: undefined,
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderRadius: 12,
    showShadow: true,
    padding: 24,
    textFontFamily: undefined,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: ServiceCardSettings,
  },
};
